// chartExport.js — LAB-ONLY chart capture + PDF/PNG download for a completed chart turn.
//
// Shared foundation for the two ChartCard export buttons: one DOM capture path
// (html-to-image toPng over the WHOLE card node — covers all chart types including the
// HTML-rendered kpi/table, which SVG serialization can't) feeding two outputs:
//   • PDF (jsPDF, client-side only — no server round-trip): title + captured chart image +
//     the buildExportMeta captionLines as SELECTABLE text + a tenant/generated footer.
//   • PNG: the captured card with the caption footer BAKED INTO the image (appended to the
//     DOM for the capture, removed after), so the applied-filter context travels with it.
//
// The caption is the engine's exportMeta.captionLines (ai:done.exports[0].exportMeta) — the
// applied context of the data the answer used (module/source, tenant, date window + basis,
// status/value filters, count), NOT a description of the chart. Org scope is not yet in
// captionLines (buildExportMeta v1 omits it — naming org ids needs a DB round-trip); when
// the engine adds it, both exports pick it up with no change here.
//
// Fonts: Inter is loaded from Google Fonts; html-to-image inlines webfont CSS at capture
// time (the fonts are CORS-served). Verified Chrome-first — Safari's foreignObject quirks
// may degrade text rendering there (html2canvas is the documented fallback if that bites).

import { toPng } from "html-to-image";
import { jsPDF } from "jspdf";

const CAPTURE_PIXEL_RATIO = 2;

// Timestamp for filenames + the PDF footer — mirrors buildExportMeta's caption format
// ("YYYY-MM-DD HH:mm UTC"), sourced from the descriptor's generatedAt when present.
function formatGeneratedAt(iso) {
  const s = String(iso || new Date().toISOString());
  return `${s.slice(0, 16).replace("T", " ")} UTC`;
}

function slugify(text, fallback) {
  const slug = String(text || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 60);
  return slug || fallback;
}

function buildFilename(spec, exportMeta, ext) {
  const tenant = (exportMeta && exportMeta.tenant && exportMeta.tenant.schema) || "chart";
  const date = String((exportMeta && exportMeta.generatedAt) || new Date().toISOString()).slice(0, 10);
  return `${tenant}_${slugify(spec && spec.title, "chart")}_${date}.${ext}`;
}

// The caption block both outputs render: the engine's applied-filter lines, then the
// chart's own caveat (the honest-basis note) when present.
function captionLinesFor(spec, exportMeta) {
  const lines = (exportMeta && Array.isArray(exportMeta.captionLines)) ? [...exportMeta.captionLines] : [];
  if (spec && spec.caveat) lines.push(`Note: ${spec.caveat}`);
  return lines;
}

// Capture the card node as a PNG data URL. `footerLines` (PNG path) are appended to the
// node as a styled footer for the duration of the capture only — inline styles, so the
// exported image carries the caption with no stylesheet dependency.
async function captureCardPng(node, footerLines) {
  let footer = null;
  if (footerLines && footerLines.length) {
    footer = document.createElement("div");
    footer.style.cssText = "margin-top:16px;padding-top:12px;border-top:1px solid #e5e7eb;font-family:Inter,-apple-system,system-ui,sans-serif;font-size:12px;line-height:1.6;color:#6b7280;";
    for (const line of footerLines) {
      const p = document.createElement("div");
      p.textContent = line;
      footer.appendChild(p);
    }
    node.appendChild(footer);
  }
  try {
    const dataUrl = await toPng(node, { pixelRatio: CAPTURE_PIXEL_RATIO, backgroundColor: "#ffffff" });
    return dataUrl;
  } finally {
    if (footer) footer.remove();
  }
}

function triggerDownload(href, filename) {
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

// PNG export: the whole card with the caption footer BAKED INTO the image, so the applied
// filters travel with the pixels wherever the image is pasted.
export async function exportChartPng({ node, spec, exportMeta }) {
  const dataUrl = await captureCardPng(node, captionLinesFor(spec, exportMeta));
  triggerDownload(dataUrl, buildFilename(spec, exportMeta, "png"));
}

// PDF export: captured card image + the caption as SELECTABLE text (not pixels), so the
// applied filters stay copyable/searchable in the document.
export async function exportChartPdf({ node, spec, exportMeta }) {
  const dataUrl = await captureCardPng(node, null);
  const img = await new Promise((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error("chart image failed to load"));
    el.src = dataUrl;
  });

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 14;
  const contentW = pageW - margin * 2;
  let y = margin;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(17, 24, 39);
  const titleLines = doc.splitTextToSize(String((spec && spec.title) || "Chart"), contentW);
  doc.text(titleLines, margin, y + 5);
  y += 5 + titleLines.length * 6;

  // Image scaled to content width; clamped to the space above the footer so a tall
  // card (e.g. a long table) still fits on the single page.
  const footerReserve = 12;
  const captionLines = captionLinesFor(spec, exportMeta);
  const captionReserve = captionLines.length * 4.5 + (captionLines.length ? 6 : 0);
  let imgW = contentW;
  let imgH = (img.height / img.width) * imgW;
  const maxImgH = pageH - y - margin - footerReserve - captionReserve;
  if (imgH > maxImgH) {
    imgH = maxImgH;
    imgW = (img.width / img.height) * imgH;
  }
  doc.addImage(dataUrl, "PNG", margin, y + 2, imgW, imgH);
  y += imgH + 8;

  if (captionLines.length) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(107, 114, 128);
    for (const line of captionLines) {
      const wrapped = doc.splitTextToSize(line, contentW);
      doc.text(wrapped, margin, y);
      y += wrapped.length * 4.5;
    }
  }

  const tenantLabel = (exportMeta && exportMeta.tenant && exportMeta.tenant.label) || null;
  const footerText = [tenantLabel, `generated ${formatGeneratedAt(exportMeta && exportMeta.generatedAt)}`, "SafetyQube AI"]
    .filter(Boolean).join(" · ");
  doc.setFontSize(8);
  doc.setTextColor(156, 163, 175);
  doc.text(footerText, margin, pageH - 8);

  doc.save(buildFilename(spec, exportMeta, "pdf"));
}
